import {useMemo, useState, useEffect} from 'react';
import {createGetOneHook} from '@/utils/createGetOneHook';
import {createFieldsHook} from '@/utils/createFieldsHook';
import RegistersApiService from '@/modules/registers/services/RegistersApiService';
import {CACHE_KEY_REGISTERS} from '@/modules/registers/contants/registers';
import {fields} from '@/modules/registers/contants/registers.form';
import SelectClient from '@/modules/registers/components/SelectClient';
import SelectEstablishment from '@/modules/registers/components/SelectEstablishment';
import {TermFilter, FilterFactory} from '@dofleini/query-builder';
import SelectFacilities from '@/modules/registers/components/SelectFacilities';
import {useTranslation} from 'react-i18next';
import {useGetOneClient} from '@/modules/clients/hooks/useClients';
import SelectTemplate from '@/modules/registers/components/SelectTemplate';
import SelectFacilityType from '../components/SelectFacilityType';
import SelectClassification from '../components/SelectClassification';
import SelectTypology from '../components/SelectTypology';
import SelectCategory from '@/modules/registers/components/SelectCategory';
import SelectPeriod from '@/modules/registers/components/SelectPeriod';
import {useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import NomenclaturesTypologiesApiService from '@/modules/settings/services/NomenclaturesTypologiesApiService';

export const useGetOneRegisters = createGetOneHook(RegistersApiService, 'id', CACHE_KEY_REGISTERS);
export const useFieldsBase = createFieldsHook('registers', fields);

export const useGetAllRegisters= (filters, params) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(filters)};
  }, [filters]);

  const {data, isLoading} = useQuery(['registers-search', filters], async () => {
    const {data} = await RegistersApiService.searchForClientsUser({filters, ...params});
    return data;
  }, queryConfig);
  return {isLoading, data};
};

export const useGetAllTypologies = (filters, params) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(filters)};
  }, [filters]);

  const {data, isLoading} = useQuery(['typologies-search', filters], async () => {
    const {data} = await NomenclaturesTypologiesApiService.search({filters, ...params});
    return data;
  }, queryConfig);
  return {isLoading, data};
};

export const useFields = (isViewMode, form) => {
  const fields = useFieldsBase(isViewMode);
  const {t} = useTranslation('registers');

  const initialValues = form.getFieldsValue(true);

  const [registerType, setRegisterType] = useState(initialValues?.areaAccess);
  const [client, setClient] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [classification, setClassification] = useState('measurement');
  const {id} = useParams();

  const {data: clientData} = useGetOneClient(client);

  useEffect(() => {
    if (!client && initialValues?.client?._id) {
      setClient(initialValues?.client?._id);
      form?.setFieldsValue({client: initialValues?.client?._id});
    }
  }, [client, form, initialValues?.client?._id, initialValues?.client?.space]);

  useEffect(() => {
    if (initialValues?.establishments) {
      setEstablishments(initialValues.establishments);
    } else {
      setEstablishments([]);
    }
  }, [initialValues?.establishments]);

  useEffect(() => {
    if ((!establishments || establishments?.length === 0) && initialValues?.establishments?.length) {
      setEstablishments(initialValues?.establishments || []);
      form?.setFieldsValue({establishments: initialValues?.establishments?.map(e => e._id) || []});
    }
  }, [establishments, form, initialValues?.establishments]);

  useEffect(() => {
    /* if(client)
      setEstablishments(initialValues?.establishments);*/
    if (initialValues?.establishments?.length && initialValues?.establishments?.length === initialValues?.establishmentsTotal) {
      form?.setFieldsValue({establishments: ['all']});
    }
  }, [client, establishments, form, initialValues?.establishments, initialValues?.establishmentsTotal]);

  useEffect(() => {
    if (initialValues?.areas?.length && initialValues?.areas?.length === initialValues?.areasTotal) {
      form?.setFieldsValue({areas: ['all']});
    } else form?.setFieldsValue({areas: initialValues?.areas});
  }, [client, establishments, form, initialValues?.areas, initialValues?.areas?.length, initialValues?.areasTotal, initialValues.establishments, initialValues.establishmentsTotal]);

  useEffect(() => {
    if (initialValues?.areaAccess) {
      setRegisterType(initialValues?.areaAccess);
      form?.setFieldsValue({typeArea: initialValues?.areaAccess});
    }
  }, [form, initialValues?.areaAccess, registerType]);

  const filterEstablishmentByClient = useMemo(() => {
    if (client) {
      if (establishments?.length && establishments?.includes('all'))
        return new TermFilter({ field: 'secureSpacePath', value: clientData?.space }).toQuery();
      return new TermFilter({field: 'secureSpacePath', value: clientData?.space, objectId: true}).toQuery();
    }
    return null;
  }, [client, clientData?.space, establishments]);

  const filterFacilitiesByEstablishment = useMemo(() => {
    if (establishments?.length && establishments?.[0] !== 'all') {
      const establishmentFilter = new TermFilter({field: 'establishment', value: {$in: establishments?.map(e => e?._id || e)}}).toQuery();
      const facilityType = new TermFilter({field: 'typeArea', value: registerType}).toQuery();
      return FilterFactory.add(facilityType, establishmentFilter);
    }
    if (establishments?.length && establishments?.includes('all')) {
      const establishmentFilter = new TermFilter({field: 'secureSpacePath', value: clientData?.space}).toQuery();
      const facilityType = new TermFilter({field: 'typeArea', value: registerType}).toQuery();
      return FilterFactory.add(facilityType, establishmentFilter);
    }
    return null;
  }, [clientData?.space, establishments, registerType]);

  const filterTemplateByRegisterTypeAndClassification = useMemo(() => {
    if (registerType) {
      const regType = new TermFilter({field: 'registerType', value: registerType}).toQuery();
      const classify = new TermFilter({field: 'classification', value: classification}).toQuery();
      return FilterFactory.add(regType, classify);
    }
    return null;
  }, [classification, registerType]);

  return useMemo(() => {
    const fieldsMap = [...fields];

    if (id && !isViewMode && initialValues?.hasData) {
      fieldsMap.splice(
        0, 0,
        {
          key: 'name',
          label: t('name'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderName'),
          onlyEdit: true
        },
      );
      fieldsMap.splice(
        1, 0,
        {
          key: 'client',
          label: t('client'),
          colSpan: 1,
          placeholder: t('placeholderClient'),
          disabled: true,
          // eslint-disable-next-line react/display-name
          widget: props => <SelectClient setClient={setClient} form={form} {...props} />
        },
      );
      fieldsMap.splice(
        2, 0,
        {
          key: 'establishments',
          label: t('establishments'),
          colSpan: 1,
          placeholder: t('placeholderEstablishment'),
          // eslint-disable-next-line react/display-name
          widget: props => <SelectEstablishment
            establishments={establishments}
            setEstablishments={setEstablishments}
            form={form}
            filters={filterEstablishmentByClient}
            establishmentTotal={initialValues?.establishmentTotal || 0}
            {...props}
            disabled
          />,
          dependencies: ['client']
        },
      );
      fieldsMap.splice(
        3, 0,
        {
          key: 'areas',
          label: t('facilities'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderFacilities'),
          // eslint-disable-next-line react/display-name
          widget: props => <SelectFacilities
            filters={filterFacilitiesByEstablishment}
            establishments={establishments}
            currentSelection={initialValues?.areas || []}
            areasTotal={initialValues?.areasTotal || 0}
            form={form}
            disabled={initialValues?.establishments?.length === 0}
            {...props}
          />,
          dependencies: ['establishments']
        },
      );
    } else if (!isViewMode && !initialValues?.hasData) {
      fieldsMap.splice(
        0, 0,
        {
          key: 'name',
          label: t('name'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderName'),
          onlyEdit: true
        },
      );
      fieldsMap.splice(
        1, 0,
        {
          key: 'areaAccess',
          label: t('facilityType'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderFacilityType'),
          onlyEdit: true,
          // eslint-disable-next-line react/display-name
          widget: props => <SelectFacilityType form={form} setRegisterType={setRegisterType} defaultValue={initialValues?.areaAccess} {...props}/>,
        },
      );
      fieldsMap.splice(
        2, 0,
        {
          key: 'classification',
          label: t('classification'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderClassification'),
          onlyEdit: true,
          initialValue: 'measurement',
          // eslint-disable-next-line react/display-name
          widget: props => <SelectClassification setClassification={setClassification} {...props}/>,
        },
      );
      fieldsMap.splice(
        3, 0,
        {
          key: 'template',
          label: t('template'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderTemplate'),
          onlyEdit: true,
          // eslint-disable-next-line react/display-name
          widget: props => <SelectTemplate defaultValue={initialValues?.template} filters={filterTemplateByRegisterTypeAndClassification}{...props}/>,
        },
      );
      fieldsMap.splice(
        4, 0,
        {
          key: 'client',
          label: t('client'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderClient'),
          // eslint-disable-next-line react/display-name
          widget: props => <SelectClient setClient={setClient} form={form} {...props} />
        },
      );
      fieldsMap.splice(
        5, 0,
        {
          key: 'establishments',
          label: t('establishments'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderEstablishment'),
          // eslint-disable-next-line react/display-name
          widget: ({...props}) => <SelectEstablishment
            setEstablishments={setEstablishments}
            form={form}
            filters={filterEstablishmentByClient}
            establishmentTotal={initialValues?.establishmentTotal || 0}
            {...props}
            disabled={!client}
          />,
          dependencies: ['client']
        },
      );
      fieldsMap.splice(
        6, 0,
        {
          key: 'areas',
          label: t('facilities'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderFacilities'),
          // eslint-disable-next-line react/display-name
          widget: props => <SelectFacilities
            filters={filterFacilitiesByEstablishment}
            establishments={establishments}
            currentSelection={initialValues?.areas || []}
            areasTotal={initialValues?.areasTotal || 0}
            {...props}
            disabled={initialValues?.establishments?.length === 0}
          />,
          dependencies: ['establishments']
        },
      );
      fieldsMap.splice(
        7, 0,
        {
          key: 'typologies',
          label: t('typology'),
          required: classification === 'measurement',
          colSpan: 1,
          placeholder: t('placeholderTypology'),
          // eslint-disable-next-line react/display-name
          widget: props => <SelectTypology {...props} />,
          dependencies: ['classification']
        },
      );
      fieldsMap.splice(
        8, 0,
        {
          key: 'category',
          label: t('category'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderCategory'),
          onlyEdit: true,
          widget: SelectCategory
        },
      );
      fieldsMap.splice(
        9, 0,
        {
          key: 'period',
          label: t('period'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderPeriod'),
          widget: SelectPeriod,
          onlyEdit: true
        },
      );
      fieldsMap.splice(
        10, 0,
        {
          key: 'frequency',
          label: t('frequency'),
          required: true,
          colSpan: 1,
          placeholder: t('placeholderFrequency'),
        },
      );
    }

    return fieldsMap;
  }, [id, classification, client, establishments, fields, filterEstablishmentByClient, filterFacilitiesByEstablishment, filterTemplateByRegisterTypeAndClassification, form, initialValues, isViewMode, t]);
};
